import type {
  BillingGetInfoResponse,
  BillingInfo,
  BillingOrdwayCouponModel,
  BillingOrdwayCustomerModel,
  BillingOrdwayProfileResponse,
  BillingOrdwaySubscriptionModel,
  Coupon,
  TaxRate,
  Tier,
} from '@customerio/hydra-ui-api';
import { atom, computed } from 'nanostores';
import { hydraUiApi } from '@/services/api';
import { AccountState } from '@/services/auth/types';
import { accountStore } from '@/services/auth/store';
import { enqueueTask } from '@/utils/enqueue-task';
import { getCrossAppCache, setCrossAppCache } from '@/utils/cross-app-cache';
import { isEmptyString } from '@/utils/string';
import type { StartupType } from './plans';
import {
  ordwaySubscriptionToBillingPlan,
  stripePlanToBillingPlan,
  stripeSubscriptionToBillingData,
  type OrdwaySubscriptionBillingPlan,
  type StripeBillingPlan,
  type StripeSubscriptionBillingData,
  type StripePlanContext,
} from './transforms';

/** Feature-flag key controlling the "new pipelines objects pricing" plan highlights. */
const NEW_PIPELINES_OBJECTS_PRICING_FLAG = 'new_pipelines_objects_pricing';

/** A single product line on an Ordway subscription (`subscription.plans[]`). */
export type OrdwaySubscriptionProduct = NonNullable<BillingOrdwaySubscriptionModel['plans']>[number];

/**
 * The effective subscription stored on {@link billing.subscription}. For Ordway accounts
 * this is the Ordway subscription transformed into the shared `BillingPlan` shape (raw
 * wire fields preserved side-by-side). For legacy Stripe accounts it is the raw Stripe
 * subscription with its `plan` resolved to a `StripeBillingPlan` and a `hasCard` flag
 * added (raw wire fields preserved side-by-side).
 */
export type BillingSubscription = StripeSubscriptionBillingData | OrdwaySubscriptionBillingPlan;

/** A coupon is either the Stripe model or the Ordway model. */
export type BillingCoupon = Coupon | BillingOrdwayCouponModel;

/** Options for {@link billing.initialize}. Mirrors the Ember billing service's `initialize` task. */
export interface InitializeBillingOptions {
  /** Re-fetch even when already initialized (subject to a short throttle). */
  refresh?: boolean;
  /** Swallow request errors and still mark the service initialized. */
  ignoreErrors?: boolean;
  /** Plan slug from a URL query param; selects {@link billing.paramPlan} from the returned plans. Bypasses the refresh throttle. */
  paramPlanSlug?: string;
  /** Whether to include coupon details in the Ordway profile response. */
  includeCoupons?: boolean;
}

/**
 * Refresh throttle (ms). Mirrors the Ember billing service: when already
 * initialized and refreshed without a coupon/plan param, suppress refreshes
 * that land within this window (the user is likely just being redirected).
 */
const RECENTLY_INITIALIZED_WINDOW_MS = 500;

/**
 * How long a billing response stays usable from the cross-app cache (ms). When the
 * shell repeatedly boots the embedded apps, each boot would otherwise re-request the
 * billing endpoints and trip 429s from the billing vendor. Caching the response lets
 * non-refresh `initialize` calls — in this app or in the embedded Ember app, which
 * shares `window.__hydra_cross_app_cache__` — reuse a recent response instead of
 * hitting the network. Mirrors the Ember billing service's `BILLING_CACHE_TTL_MS`.
 */
const BILLING_CACHE_TTL_MS = 30_000;

/**
 * Loads the Ordway billing profile, reusing a recent cross-app-cached response when
 * available. Non-refresh calls read the cache first; a cache miss (or `refresh`) goes
 * to the network and writes the response back. The cache key matches the Ember billing
 * service exactly so the two apps share cached responses.
 */
async function loadOrdwayProfile(
  accountId: number,
  includeCoupons: boolean,
  refresh: boolean,
): Promise<BillingOrdwayProfileResponse> {
  const cacheKey = `/accounts/${accountId}/billing/profile?coupons=${includeCoupons}`;
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  if (cached != null) {
    return cached as BillingOrdwayProfileResponse;
  }

  const res = await hydraUiApi.billing.getProfile({
    params: { path: { account_id: String(accountId) }, query: { coupons: String(includeCoupons) } },
  });
  setCrossAppCache({ key: cacheKey, value: res, ttl: BILLING_CACHE_TTL_MS });
  return res;
}

/**
 * Loads the legacy Stripe billing info, reusing a recent cross-app-cached response when
 * available (see {@link loadOrdwayProfile}). The `code=` segment in the key is
 * intentionally empty — coupon-code / `extra_plan` request params are not yet wired up
 * here — but still matches the Ember key for the common (no coupon code) boot path.
 */
async function loadStripeInfo(
  accountId: number,
  paramPlanSlug: string,
  refresh: boolean,
): Promise<BillingGetInfoResponse> {
  const cacheKey = `/accounts/${accountId}/billing/info?code=&extra_plan=${paramPlanSlug}`;
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  if (cached != null) {
    return cached as BillingGetInfoResponse;
  }

  const res = await hydraUiApi.billing.getInfo({
    params: { path: { account_id: String(accountId) } },
  });
  setCrossAppCache({ key: cacheKey, value: res, ttl: BILLING_CACHE_TTL_MS });
  return res;
}

/**
 * Sorts Ordway subscriptions ascending by service start date, oldest first.
 * Subscriptions without a start date sort last (matching lodash `sortBy`).
 */
function sortOrdwaySubscriptionsByDate(
  subscriptions: readonly BillingOrdwaySubscriptionModel[],
): BillingOrdwaySubscriptionModel[] {
  return [...subscriptions].sort((a, b) => {
    const aTime = isEmptyString(a.service_start_date) ? Infinity : Date.parse(a.service_start_date ?? '');
    const bTime = isEmptyString(b.service_start_date) ? Infinity : Date.parse(b.service_start_date ?? '');
    return aTime - bTime;
  });
}

/**
 * The backend does not filter future-subscription products the way it filters
 * the effective subscription's, so collapse duplicate `product_id`s here. When
 * a product has no charge start date it's aligned with the service start date
 * and wins; otherwise the earliest charge start date wins. Ports Ember
 * `#setOrdwayBillingProperties`.
 */
function dedupeFutureSubscriptionProducts(products: readonly OrdwaySubscriptionProduct[]): OrdwaySubscriptionProduct[] {
  const productById: Record<string, OrdwaySubscriptionProduct> = {};

  for (const product of products) {
    const existing = productById[product.product_id];

    if (existing == null) {
      productById[product.product_id] = product;
      continue;
    }

    if (isEmptyString(product.charge_start_date)) {
      // No charge start date means the product is aligned with the service
      // start date — prefer it.
      productById[product.product_id] = product;
    } else if (!isEmptyString(existing.charge_start_date)) {
      // Both have a charge start date: keep the earliest.
      if (Date.parse(product.charge_start_date ?? '') < Date.parse(existing.charge_start_date ?? '')) {
        productById[product.product_id] = product;
      }
    }
  }

  return Object.values(productById);
}

/**
 * Singleton billing service for the Hydra React app.
 *
 * Modelled as a plain object — a curated public API rather than a class or a
 * module that re-exports everything — following the "limiting public APIs"
 * pattern (https://zenn.dev/dk_/articles/bc779b8fba68b0?locale=en#limiting-public-apis).
 *
 * State is held in nanostore atoms so the UI can subscribe and re-render.
 * Account-derived flags are computeds over the shared `accountStore`, so they
 * track account changes without any explicit wiring. Exposed on
 * `window.__hydra__.billing` for cross-app (Ember) consumption.
 */
/**
 * Builds the {@link StripePlanContext} used to transform Stripe plans. In Ember these
 * values came from the billing/features services via getters; here they're captured once
 * from the billing info and account at load time, keeping the transform pure. Mirrors the
 * Ember `PlanModel` getters: `canCancel` ← `billingInfo.cancellable`, the feature-group
 * selection ← `billing.isTrial` (which is `premium_trial || basic_trial`, or the account's
 * `suspend_trial_at` when there is no billing info) and `billingInfo.startupProgram`, and
 * the highlights ← the `new_pipelines_objects_pricing` feature flag.
 */
// fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
function stripePlanContext(billingInfo: BillingInfo | undefined): StripePlanContext {
  const account = accountStore.get();
  const isAccountTrial =
    billingInfo == null ? account?.suspend_trial_at != null : billingInfo.basic_trial || billingInfo.premium_trial;

  return {
    cancellable: billingInfo?.cancellable ?? false,
    isAccountTrial,
    startupProgram: isEmptyString(billingInfo?.startup_program)
      ? undefined
      : (billingInfo?.startup_program as StartupType),
    newPipelinesObjectsPricing: account?.feature_flags?.[NEW_PIPELINES_OBJECTS_PRICING_FLAG] ?? false,
  };
}

export const billing = {
  isOrdwayCustomer: computed(accountStore, (account) => account?.ordway_id != null && account.ordway_id.length > 0),
  isOrdwayCustomerWaitingInitialization: computed(
    accountStore,
    (account) => account?.ordway_id?.startsWith('TEMP-') === true,
  ),

  initialized: atom(false),
  initializedAt: null as number | null,

  subscription: atom<BillingSubscription | undefined>(undefined),
  coupon: atom<BillingCoupon | undefined>(undefined),
  taxRate: atom<TaxRate | undefined>(undefined),

  /** Plan matching `paramPlanSlug`, resolved from {@link billing.plans}. */
  paramPlan: atom<StripeBillingPlan | null>(null),

  /** Legacy stripe billing info. Undefined for Ordway accounts. */
  billingInfo: atom<BillingInfo | undefined>(undefined),
  /** Stripe plans transformed into the shared `BillingPlan` shape. Undefined for Ordway accounts. */
  plans: atom<StripeBillingPlan[] | undefined>(undefined),
  tiers: atom<Tier[] | undefined>(undefined),

  /** Ordway billing data. Undefined for legacy stripe and metronome accounts. */
  customer: atom<BillingOrdwayCustomerModel | undefined>(undefined),
  /** The upcoming Ordway subscription, transformed into the shared `BillingPlan` shape. */
  futureSubscription: atom<OrdwaySubscriptionBillingPlan | undefined>(undefined),
  products: atom<OrdwaySubscriptionProduct[] | undefined>(undefined),

  setStripeBillingProperties(res: BillingGetInfoResponse, paramPlanSlug: string) {
    const context = stripePlanContext(res.billing_info);
    const plans = (res.plans ?? []).map((plan) => stripePlanToBillingPlan(plan, context));

    this.billingInfo.set(res.billing_info ?? undefined);
    this.subscription.set(
      res.subscription == null ? undefined : stripeSubscriptionToBillingData(res.subscription, plans),
    );
    this.coupon.set(res.coupon ?? undefined);
    this.plans.set(plans);
    this.tiers.set(res.tiers ?? []);
    this.taxRate.set(res.tax_rate ?? undefined);
    this.paramPlan.set(
      isEmptyString(paramPlanSlug) ? null : (plans.find((plan) => plan.slug === paramPlanSlug) ?? null),
    );
    // reset ordway properties to clear stale data
    this.customer.set(undefined);
    this.futureSubscription.set(undefined);
    this.products.set(undefined);
  },

  // fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
  setOrdwayBillingProperties(res: BillingOrdwayProfileResponse) {
    const allSubscriptions = sortOrdwaySubscriptionsByDate(res.ordway_subscriptions ?? []);

    const subscription = allSubscriptions[0];
    let futureSubscription = allSubscriptions.length > 1 ? allSubscriptions[1] : undefined;

    if (futureSubscription != null) {
      futureSubscription = {
        ...futureSubscription,
        plans: dedupeFutureSubscriptionProducts(futureSubscription.plans ?? []),
      };
    }

    this.customer.set(res.ordway_customer ?? undefined);
    this.subscription.set(subscription == null ? undefined : ordwaySubscriptionToBillingPlan(subscription));
    this.futureSubscription.set(
      futureSubscription == null ? undefined : ordwaySubscriptionToBillingPlan(futureSubscription),
    );
    this.coupon.set(res.coupon ?? undefined);
    this.products.set(subscription?.plans ?? []);
    // reset legacy stripe properties to clear stale data
    this.billingInfo.set(undefined);
    this.plans.set(undefined);
    this.tiers.set(undefined);
  },

  /**
   * Loads billing data for the current account into the stores above.
   *
   * An enqueued task (à la ember-concurrency): call `billing.initialize.perform(...)`.
   * Concurrent callers are serialized so they share one in-flight load; `perform`
   * is a closure, so it stays callable when destructured or invoked off the window
   * global. The body references `billing` directly (not `this`) since it runs
   * outside the object's method context.
   */
  initialize: enqueueTask(
    // fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
    async ({
      refresh = false,
      ignoreErrors = false,
      paramPlanSlug = '',
      includeCoupons = false,
    }: InitializeBillingOptions = {}): Promise<void> => {
      if (billing.initialized.get() && !refresh) {
        return;
      }

      // If the service was recently initialized, return early even when refreshing,
      // since the user probably hasn't taken an action that warrants a refresh
      // (we're likely just redirecting). A plan param means the data would differ,
      // so don't short-circuit it.
      const initializedAt = billing.initializedAt ?? 0;
      const recentlyInitialized = Date.now() - initializedAt <= RECENTLY_INITIALIZED_WINDOW_MS;

      if (billing.initialized.get() && refresh && paramPlanSlug === '' && recentlyInitialized) {
        return;
      }

      const account = accountStore.get();
      if (account == null) {
        return;
      }

      const isOrdway = billing.isOrdwayCustomer.get();

      // The Ordway profile endpoint is expected to fail for suspended/canceled
      // accounts and for customers still waiting on Ordway provisioning, so widen
      // `ignoreErrors` for those cases (matching the Ember service).
      const ordwayIgnoreErrors =
        ignoreErrors ||
        account.account_state === AccountState.Suspended ||
        account.account_state === AccountState.Canceled ||
        billing.isOrdwayCustomerWaitingInitialization.get();

      const effectiveIgnoreErrors = isOrdway ? ordwayIgnoreErrors : ignoreErrors;

      try {
        if (isOrdway) {
          const res = await loadOrdwayProfile(account.id, includeCoupons, refresh);
          billing.setOrdwayBillingProperties(res);
        } else {
          const res = await loadStripeInfo(account.id, paramPlanSlug, refresh);
          billing.setStripeBillingProperties(res, paramPlanSlug);
        }

        billing.initialized.set(true);
        billing.initializedAt = Date.now();
      } catch (error) {
        if (effectiveIgnoreErrors) {
          billing.initialized.set(true);
          billing.initializedAt = Date.now();
          return;
        }
        throw error;
      }
    },
  ),
};

/** Public API surface of the Hydra billing service. */
export type BillingService = typeof billing;
